import React, { useState, useEffect } from "react";
import axios from "axios";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Activity, Cpu, DollarSign, Clock, Zap, BarChart3, 
  CheckCircle, XCircle, RefreshCw, Server, GitBranch, Loader2
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MLOpsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/mlops/dashboard`);
      setData(res.data);
    } catch (e) {
      console.error("MLOps fetch error:", e);
    }
    setLoading(false);
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const model = data?.current_model || {};
  const daily = data?.daily_stats || [];
  const abResults = data?.ab_test_results || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <Header />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Cpu className="h-8 w-8" />
                <h1 className="text-3xl font-bold">MLOps Dashboard</h1>
              </div>
              <p className="opacity-90">Nova AI Performance & Model Management</p>
            </div>
            <Button onClick={fetchData} disabled={refreshing} variant="secondary">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        
        {/* Current Model Status */}
        <Card className="bg-gray-800/50 border-gray-700 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/20 rounded-xl">
                  <Server className="h-8 w-8 text-green-500" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">{model.model_id || "nova-v1.0"}</h3>
                  <p className="text-gray-400">{model.model_name || "gpt-4o-mini"} • v{model.version || "1.0.0"}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Badge className="bg-green-600 text-white px-4 py-1">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {model.status || "Production"}
                </Badge>
                <Badge variant="outline" className="text-gray-300 border-gray-600">
                  {model.provider || "OpenAI"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <Activity className="h-6 w-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{metrics.total_inferences || 0}</p>
              <p className="text-xs text-gray-400">Total Inferences</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <Zap className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{metrics.last_7_days || 0}</p>
              <p className="text-xs text-gray-400">Last 7 Days</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{metrics.success_rate || 100}%</p>
              <p className="text-xs text-gray-400">Success Rate</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <Clock className="h-6 w-6 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{metrics.avg_latency_ms || 0}ms</p>
              <p className="text-xs text-gray-400">Avg Latency</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <BarChart3 className="h-6 w-6 text-orange-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{(metrics.total_tokens || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-400">Total Tokens</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <DollarSign className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">${metrics.total_cost_usd || 0}</p>
              <p className="text-xs text-gray-400">Total Cost</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="performance" className="space-y-4">
          <TabsList className="bg-gray-800 border-gray-700">
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="ab-testing">A/B Testing</TabsTrigger>
            <TabsTrigger value="models">Model Registry</TabsTrigger>
            <TabsTrigger value="deployments">Deployments</TabsTrigger>
          </TabsList>

          {/* Performance Tab */}
          <TabsContent value="performance">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Daily Inference Volume</CardTitle>
                </CardHeader>
                <CardContent>
                  {daily.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={daily}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="_id" stroke="#9CA3AF" fontSize={12} />
                        <YAxis stroke="#9CA3AF" fontSize={12} />
                        <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} />
                        <Bar dataKey="calls" fill="#10B981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-400 text-center py-8">No data yet</p>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Daily Cost ($)</CardTitle>
                </CardHeader>
                <CardContent>
                  {daily.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={daily}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="_id" stroke="#9CA3AF" fontSize={12} />
                        <YAxis stroke="#9CA3AF" fontSize={12} />
                        <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} />
                        <Line type="monotone" dataKey="cost" stroke="#8B5CF6" strokeWidth={2} dot={{ fill: '#8B5CF6' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-400 text-center py-8">No data yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* A/B Testing Tab */}
          <TabsContent value="ab-testing">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <GitBranch className="h-5 w-5 text-purple-500" />
                  A/B Test Results
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Compare model performance across test groups
                </CardDescription>
              </CardHeader>
              <CardContent>
                {abResults.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {abResults.map((group, i) => (
                      <div key={i} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-white font-semibold">Group {group._id || 'A'}</h4>
                          <Badge className="bg-purple-600">{group.calls || 0} calls</Badge>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Success Rate</span>
                            <span className="text-white">{(group.success_rate || 0).toFixed(1)}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Avg Latency</span>
                            <span className="text-white">{(group.avg_latency || 0).toFixed(0)}ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Quality Score</span>
                            <span className="text-white">{(group.avg_quality || 0).toFixed(1)}/10</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-8">No A/B tests running. Start a test from Admin panel.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Model Registry Tab */}
          <TabsContent value="models">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Model Registry</CardTitle>
                <CardDescription className="text-gray-400">All registered model versions</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.model_registry?.length > 0 ? (
                  <div className="space-y-3">
                    {data.model_registry.map((m, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                        <div>
                          <h4 className="text-white font-medium">{m.model_id}</h4>
                          <p className="text-gray-400 text-sm">{m.model_name} • v{m.version}</p>
                        </div>
                        <Badge className={m.status === 'production' ? 'bg-green-600' : 'bg-gray-600'}>
                          {m.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Server className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">Default model (nova-v1.0) in production</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deployments Tab */}
          <TabsContent value="deployments">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Deployment History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-white font-medium">Current Deployment: {model.model_id || "nova-v1.0"}</p>
                  <p className="text-gray-400 text-sm mt-1">Deployed: {model.deployment_date || "2026-01-09"}</p>
                  <p className="text-gray-400 text-sm">Status: Healthy</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MLOpsDashboard;
